import {
  DEFAULT_RESTAURANT_EXEMPLE_PROPS,
  defaultPropsForRestaurantVariant,
  migrateRestaurantSiteSections,
} from './restaurantExempleDefaults';
import { repairJardinsKitSectionMeta, stripDuplicateJardinsPageMenu } from './designKit';
import { buildReservationDayBookingsFromRequests } from './commerceAdmin';
import {
  normalizeSiteTypography,
  resolveSiteFontStyle,
  resolveInheritedFontStyle,
  resolveFontFamilyChoice,
} from './siteFonts';
import { normalizeAmbientMusic } from './ambientMusic';

export const GRID_COLUMNS = 48;
export const GRID_ROW_HEIGHT = 16;
export const LEGACY_GRID_COLUMNS = 12;
export const LEGACY_GRID_ROW_HEIGHT = 56;
export const V2_GRID_COLUMNS = 24;
export const V2_GRID_ROW_HEIGHT = 32;
export const DESKTOP_WIDTH = 1200;
export const TABLET_WIDTH = 768;
export const MOBILE_WIDTH = 390;
export const SCROLL_PADDING_BOTTOM = 'min(40vh, 480px)';

export const VIEWPORTS = {
  desktop: { id: 'desktop', width: DESKTOP_WIDTH, label: 'Ordinateur', minWidth: 1024 },
  tablet: { id: 'tablet', width: TABLET_WIDTH, label: 'Tablette', minWidth: 640 },
  mobile: { id: 'mobile', width: MOBILE_WIDTH, label: 'Téléphone', minWidth: 0 },
};

export const VIEWPORT_LIST = [VIEWPORTS.desktop, VIEWPORTS.tablet, VIEWPORTS.mobile];

export const VIEWPORT_IDS = VIEWPORT_LIST.map((v) => v.id);

/** Empilement éditeur — toujours sous les modales (9000+). */
export const EDITOR_Z_INDEX = {
  menu: 20,
  menuSelected: 25,
  componentSelectedBoost: 50,
  componentDraggingBoost: 100,
  modal: 9000,
  preview: 9100,
};

export const DEFAULT_BACKGROUND = {
  type: 'color',
  color: '#ffffff',
  imageUrl: '',
  imageSize: 'cover',
  imagePosition: 'center',
};

export const DEFAULT_SEO = {
  shareTitle: '',
  shareDescription: '',
  shareImageUrl: '',
};

/** GEO — Generative Engine Optimization (indexation par les moteurs IA). */
export const DEFAULT_GEO = {
  enabled: true,
  allowTraining: false,
  summary: '',
  topics: '',
  entityName: '',
  entityType: 'organization',
  keyFacts: '',
  audience: '',
  language: 'fr',
};

export const GEO_ENTITY_TYPES = [
  { id: 'organization', label: 'Organisation / Entreprise' },
  { id: 'localBusiness', label: 'Commerce local / Cabinet' },
  { id: 'person', label: 'Personne / Indépendant' },
  { id: 'product', label: 'Produit / Marque' },
  { id: 'service', label: 'Service' },
  { id: 'website', label: 'Site web / Blog' },
];

export const DEFAULT_TYPOGRAPHY = {
  fontFamily: '',
  fontUrl: '',
  applyToSite: false,
  customFonts: [],
  defaultFontId: null,
};

import {
  normalizeColorFill,
  resolveColorFillCss,
  resolveColorFillStyle,
  resolveTextFillStyle,
  resolveShapeRadius,
} from './colorFillUtils';

export {
  normalizeColorFill,
  resolveColorFillCss,
  resolveColorFillStyle,
  resolveTextFillStyle,
  resolveShapeRadius,
} from './colorFillUtils';

export const DEFAULT_TEXT_HTML = '<p>Votre texte ici</p>';

export const DEFAULT_COMPONENT_BACKGROUND = {
  enabled: false,
  type: 'color',
  color: '#ffffff',
  imageUrl: '',
  imageSize: 'cover',
  imagePosition: 'center',
};

export function getMenuSections(sections, menuComp) {
  return getMenuTabs(sections, menuComp)
    .filter((t) => t.kind === 'section')
    .map((t) => (sections || []).find((s) => s.id === t.id))
    .filter(Boolean);
}

export function defaultMenuSectionOrder(sections) {
  return (sections || []).map((s) => s.id);
}

export function normalizeExternalUrl(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s.replace(/^\/+/, '')}`;
}

export function buildDefaultTabOrder(sections, externalLinks = [], menuButtons = []) {
  const secIds = defaultMenuSectionOrder(sections);
  const linkIds = (externalLinks || []).map((l) => l.id).filter(Boolean);
  const buttonIds = (menuButtons || []).map((b) => b.id).filter(Boolean);
  return [
    ...secIds.map((id) => `section:${id}`),
    ...linkIds.map((id) => `link:${id}`),
    ...buttonIds.map((id) => `button:${id}`),
  ];
}

function buildMenuLinkTabEntry(link) {
  const openMode = link.openMode || 'external';
  return {
    kind: 'link',
    id: link.id,
    label: link.label,
    url: link.url,
    openMode,
    sectionAnchor:
      openMode === 'section'
        ? String(link.sectionAnchor || '').replace(/^#/, '')
        : undefined,
    publicPath: openMode === 'publicPath' ? link.url : undefined,
  };
}

function buildMenuButtonTabEntry(btn) {
  const linkType = btn.linkType || 'section';
  return {
    kind: 'button',
    id: btn.id,
    label: btn.label,
    linkType,
    sectionAnchor: String(btn.sectionAnchor || '').replace(/^#/, ''),
    url: btn.url,
    publicPath: btn.publicPath,
  };
}

export function menuTabBarAlignRight(menuProps, tabKey) {
  return menuProps?.tabAppearances?.[tabKey]?.barAlign === 'right';
}

/** Sépare les onglets « épinglés à droite » du reste (barre ordinateur). */
export function splitMenuTabsByBarAlign(tabs, menuProps) {
  const main = [];
  const right = [];
  for (const tab of tabs || []) {
    const key = `${tab.kind}:${tab.id}`;
    if (menuTabBarAlignRight(menuProps, key)) right.push(tab);
    else main.push(tab);
  }
  return { main, right };
}

export function resolveMenuTabOrder(sections, menuProps) {
  const props = menuProps || {};
  const list = sections || [];
  const links = props.externalLinks || [];
  const buttons = props.menuButtons || [];
  const secById = new Map(list.map((s) => [s.id, s]));
  const linkById = new Map(links.map((l) => [l.id, l]));
  const buttonById = new Map(buttons.map((b) => [b.id, b]));

  let keys = [];
  if (Array.isArray(props.tabOrder) && props.tabOrder.length > 0) {
    keys = [...props.tabOrder];
  } else if (Array.isArray(props.sectionOrder) && props.sectionOrder.length > 0) {
    keys = [
      ...props.sectionOrder.map((id) => `section:${id}`),
      ...links.map((l) => `link:${l.id}`),
      ...buttons.map((b) => `button:${b.id}`),
    ];
  } else {
    keys = buildDefaultTabOrder(list, links, buttons);
  }

  const seen = new Set();
  const entries = [];

  for (const key of keys) {
    let kind;
    let id;
    if (key.startsWith('section:')) {
      kind = 'section';
      id = key.slice(8);
    } else if (key.startsWith('link:')) {
      kind = 'link';
      id = key.slice(5);
    } else if (key.startsWith('button:')) {
      kind = 'button';
      id = key.slice(7);
    } else {
      kind = 'section';
      id = key;
    }
    const token = `${kind}:${id}`;
    if (seen.has(token)) continue;
    if (kind === 'section' && secById.has(id)) {
      const sec = secById.get(id);
      entries.push({ kind: 'section', id, anchor: sec.anchor, label: sec.label });
      seen.add(token);
    } else if (kind === 'link' && linkById.has(id)) {
      entries.push(buildMenuLinkTabEntry(linkById.get(id)));
      seen.add(token);
    } else if (kind === 'button' && buttonById.has(id)) {
      entries.push(buildMenuButtonTabEntry(buttonById.get(id)));
      seen.add(token);
    }
  }

  for (const sec of list) {
    const token = `section:${sec.id}`;
    if (!seen.has(token)) {
      entries.push({ kind: 'section', id: sec.id, anchor: sec.anchor, label: sec.label });
    }
  }
  for (const link of links) {
    const token = `link:${link.id}`;
    if (!seen.has(token)) {
      entries.push(buildMenuLinkTabEntry(link));
    }
  }
  for (const btn of buttons) {
    const token = `button:${btn.id}`;
    if (!seen.has(token)) {
      entries.push(buildMenuButtonTabEntry(btn));
    }
  }

  return entries;
}

function menuUsesHamburgerLayout(menuProps, viewport = 'desktop') {
  if (viewport === 'desktop') return false;
  return menuProps?.mobileMenuStyle !== 'tabs';
}

/** Onglets visibles dans le menu (ordre personnalisé sur barre / onglets mobile ; hamburger = ordre site par défaut). */
export function getMenuTabs(sections, menuComp, options = {}) {
  const viewport = options.viewport || 'desktop';
  const props = menuComp?.props || {};
  const list = sections || [];

  const orderProps = menuUsesHamburgerLayout(props, viewport)
    ? { ...props, tabOrder: undefined, sectionOrder: undefined }
    : props;

  const hiddenFromMenu = new Set(
    Array.isArray(props.hiddenMenuSectionIds) ? props.hiddenMenuSectionIds : [],
  );

  return resolveMenuTabOrder(list, orderProps).filter((entry) => {
    if (entry.kind === 'link' || entry.kind === 'button') return true;
    if (hiddenFromMenu.has(entry.id)) return false;
    const sec = list.find((s) => s.id === entry.id);
    if (sec && isSectionHidden(sec, viewport)) return false;
    return true;
  });
}

/** Entrées pour l’éditeur d’ordre du menu (sections masquées du menu incluses). */
export function getMenuTabOrderEditorEntries(sections, menuProps) {
  return resolveMenuTabOrder(sections || [], menuProps || {});
}

export function viewportCanvasWidth(viewMode) {
  if (viewMode === 'tablet') return TABLET_WIDTH;
  if (viewMode === 'mobile') return MOBILE_WIDTH;
  return null;
}

export function uid(prefix = 'id') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function resolveBackgroundStyle(bg, fallbackColor = '#ffffff') {
  if (!bg) return resolveColorFillStyle(fallbackColor, fallbackColor);
  if (bg.type === 'image' && bg.imageUrl) {
    const under = bg.fill ?? bg.color ?? fallbackColor;
    const underStyle = resolveColorFillStyle(under, fallbackColor);
    const imgLayer = `url(${bg.imageUrl})`;
    return {
      backgroundColor: underStyle.backgroundColor || fallbackColor,
      backgroundImage: underStyle.backgroundImage
        ? `${imgLayer}, ${underStyle.backgroundImage}`
        : imgLayer,
      backgroundSize: `${bg.imageSize || 'cover'}, auto`,
      backgroundPosition: `${bg.imagePosition || 'center'}, center`,
      backgroundRepeat: 'no-repeat',
    };
  }
  const fill = bg.fill ?? bg.color ?? fallbackColor;
  return resolveColorFillStyle(fill, fallbackColor);
}

export function resolveButtonStyle(props, siteTypography = null) {
  const {
    backgroundColor,
    textColor,
    shape,
    borderRadius,
    width,
    borderWidth,
    borderColor,
    fontSize,
    fontWeight,
    inheritSiteButtonFont,
    buttonFontFamily,
  } = props || {};
  const radius = resolveShapeRadius(shape, borderRadius);
  const isCircle = shape === 'circle';
  const text = {
    ...resolveTextFillStyle(textColor, '#ffffff'),
    fontSize: fontSize ? `${fontSize}px` : undefined,
    fontWeight: fontWeight || undefined,
  };
  if (siteTypography) {
    if (inheritSiteButtonFont !== false) {
      Object.assign(text, resolveInheritedFontStyle(siteTypography));
    } else {
      text.fontFamily = resolveFontFamilyChoice(buttonFontFamily || 'system', siteTypography);
    }
  }
  return {
    button: {
      ...resolveColorFillStyle(backgroundColor, '#2563eb'),
      borderRadius: `${radius}px`,
      borderWidth: borderWidth ? `${borderWidth}px` : undefined,
      borderStyle: borderWidth ? 'solid' : undefined,
      borderColor: borderWidth ? resolveColorFillCss(borderColor, 'transparent') : undefined,
      width: width === 'full' ? '100%' : undefined,
      ...(isCircle
        ? { aspectRatio: '1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }
        : {}),
    },
    text,
  };
}

export function resolveDropdownStyle(props, siteTypography = null) {
  const { menuBackgroundColor, menuTextColor } = props || {};
  const { button: triggerStyle, text: textStyle } = resolveButtonStyle(props, siteTypography);
  return {
    trigger: triggerStyle,
    text: textStyle,
    menu: {
      ...resolveColorFillStyle(menuBackgroundColor, '#ffffff'),
      borderWidth: props?.borderWidth ? `${props.borderWidth}px` : '1px',
      borderStyle: 'solid',
      borderColor: resolveColorFillCss(props?.borderColor, '#e5e7eb'),
    },
    menuItem: resolveTextFillStyle(menuTextColor, '#1c1917'),
  };
}

export function resolveBadgeStyle(props) {
  const { backgroundColor, textColor, shape, borderRadius, fontSize, fontWeight } = props || {};
  const radius = resolveShapeRadius(shape, borderRadius);
  return {
    badge: {
      ...resolveColorFillStyle(backgroundColor, '#2563eb'),
      borderRadius: shape === 'pill' ? '9999px' : `${radius}px`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.35em 0.85em',
      lineHeight: 1.3,
      whiteSpace: 'nowrap',
    },
    text: {
      ...resolveTextFillStyle(textColor, '#ffffff'),
      fontSize: fontSize ? `${fontSize}px` : '12px',
      fontWeight: fontWeight || 600,
    },
  };
}

export function resolveSectionBackground(section) {
  const sectionType = getSectionType(section);
  if (sectionType !== 'normal') {
    const bg = section?.background || {};
    const under = {
      type: 'color',
      fill: bg.fill ?? bg.color ?? section?.backgroundColor ?? '#ffffff',
      color: bg.color ?? section?.backgroundColor ?? '#ffffff',
    };
    return resolveBackgroundStyle(under, section?.backgroundColor || '#ffffff');
  }
  if (section?.background) return resolveBackgroundStyle(section.background);
  return { backgroundColor: section?.backgroundColor || '#ffffff' };
}

export const SECTION_TYPE_LABELS = {
  normal: 'Section couleur',
  image: 'Section image',
  video: 'Section vidéo',
};

export function getSectionType(section) {
  const t = section?.sectionType;
  if (t === 'normal' || t === 'image' || t === 'video') return t;
  if (section?.background?.type === 'video') return 'video';
  if (section?.background?.type === 'image') return 'image';
  return 'normal';
}

export function createSectionBackground(sectionType = 'normal') {
  const fill = { mode: 'solid', color: '#ffffff', alpha: 1 };
  if (sectionType === 'image') {
    return {
      type: 'image',
      imageUrl: '',
      imageSize: 'fill',
      imagePosition: 'center',
      fill,
      mediaWidth: 0,
      mediaHeight: 0,
    };
  }
  if (sectionType === 'video') {
    return {
      type: 'video',
      videoUrl: '',
      posterUrl: '',
      muted: true,
      loop: true,
      fill: { mode: 'solid', color: '#000000', alpha: 1 },
      mediaWidth: 0,
      mediaHeight: 0,
    };
  }
  return {
    type: 'color',
    color: '#ffffff',
    fill,
    imageUrl: '',
    imageSize: 'cover',
    imagePosition: 'center',
  };
}

export function resolveComponentBackground(props) {
  const bg = props?.componentBackground;
  if (!bg?.enabled) return {};
  return resolveBackgroundStyle(bg, 'transparent');
}

/** Padding interne du composant (px). paddingX/Y ou côtés individuels. */
export {
  normalizeSiteTypography,
  resolveSiteFontStyle,
  resolveInheritedFontStyle,
  buildSiteFontFaceCss,
  buildSiteFontsCss,
  siteHasCustomFont,
  siteHasCustomFonts,
  getSiteCustomFonts,
  getSiteDefaultFont,
  resolveFontFamilyChoice,
  buildEditorFontWhitelist,
  resolveDefaultQuillFontKey,
  htmlForQuillEditorDisplay,
  buildFontFamilySelectOptions,
  quillFontKeyForCustomFont,
  customFontChoiceValue,
} from './siteFonts';

/** Taille des libellés d’onglets (px). `menuTabFontSize` prioritaire, sinon `menuFontSize`. */
export function resolveMenuTabFontSizePx(menuProps) {
  const raw = menuProps?.menuTabFontSize ?? menuProps?.menuFontSize;
  const size = Number(raw);
  if (Number.isFinite(size) && size >= 8 && size <= 96) return Math.round(size);
  return 14;
}

/** Police du menu (famille uniquement — la taille des onglets est sur les boutons). */
export function resolveMenuTypographyStyle(menuProps, siteTypography) {
  const styles = {};

  if (menuProps?.inheritSiteMenuFont !== false) {
    const inherited =
      resolveSiteFontStyle(siteTypography)?.fontFamily ||
      resolveInheritedFontStyle(siteTypography)?.fontFamily;
    styles.fontFamily = inherited || 'system-ui, sans-serif';
    return styles;
  }

  styles.fontFamily = resolveFontFamilyChoice(
    menuProps?.menuFontFamily || 'system',
    siteTypography,
  );
  return styles;
}

/** Typographie appliquée aux onglets du menu. */
export function resolveMenuTabTypographyStyle(menuProps, siteTypography) {
  const base = resolveMenuTypographyStyle(menuProps, siteTypography);
  return {
    fontFamily: base.fontFamily,
    fontSize: `${resolveMenuTabFontSizePx(menuProps)}px`,
  };
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const TEXT_CONTENT_KEYS = new Set([
  'inheritSiteFont',
  'textFontFamily',
  'contentFormat',
  'content',
  'componentBackground',
  'paddingX',
  'paddingY',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontSize',
  'color',
  'textAlign',
  'fontWeight',
]);

function withPreservedComponentProps(props, normalized) {
  if (!props) return normalized;
  const extras = {};
  for (const [key, value] of Object.entries(props)) {
    if (!TEXT_CONTENT_KEYS.has(key) && value !== undefined) {
      extras[key] = value;
    }
  }
  return { ...normalized, ...extras };
}

export function normalizeTextProps(props) {
  if (!props) return props;
  const content = props.content;
  if (props.contentFormat === 'html' || (content && /<[a-z][\s\S]*>/i.test(content))) {
    return withPreservedComponentProps(props, {
      inheritSiteFont: props.inheritSiteFont !== false,
      textFontFamily: props.textFontFamily || 'system',
      contentFormat: 'html',
      content: content || DEFAULT_TEXT_HTML,
      componentBackground: props.componentBackground,
      paddingX: props.paddingX,
      paddingY: props.paddingY,
      paddingTop: props.paddingTop,
      paddingRight: props.paddingRight,
      paddingBottom: props.paddingBottom,
      paddingLeft: props.paddingLeft,
    });
  }
  const style = [];
  if (props.fontSize) style.push(`font-size:${props.fontSize}px`);
  if (props.color) style.push(`color:${props.color}`);
  if (props.textAlign) style.push(`text-align:${props.textAlign}`);
  if (props.fontWeight && props.fontWeight !== 'normal') style.push(`font-weight:${props.fontWeight}`);
  const styleAttr = style.length ? ` style="${style.join(';')}"` : '';
  return withPreservedComponentProps(props, {
    inheritSiteFont: props.inheritSiteFont !== false,
    textFontFamily: props.textFontFamily || 'system',
    contentFormat: 'html',
    content: content
      ? `<p${styleAttr}>${escapeHtml(content).replace(/\n/g, '<br>')}</p>`
      : DEFAULT_TEXT_HTML,
    componentBackground: props.componentBackground,
    paddingX: props.paddingX,
    paddingY: props.paddingY,
    paddingTop: props.paddingTop,
    paddingRight: props.paddingRight,
    paddingBottom: props.paddingBottom,
    paddingLeft: props.paddingLeft,
  });
}

export function resolveComponentPadding(props) {
  const p = props || {};
  if (p.padding != null && p.padding !== '') {
    const v = Math.max(0, Number(p.padding) || 0);
    return {
      paddingTop: v,
      paddingRight: v,
      paddingBottom: v,
      paddingLeft: v,
    };
  }
  const px = Math.max(0, Number(p.paddingX) || 0);
  const py = Math.max(0, Number(p.paddingY) || 0);
  return {
    paddingTop: p.paddingTop != null && p.paddingTop !== '' ? Math.max(0, Number(p.paddingTop) || 0) : py,
    paddingRight:
      p.paddingRight != null && p.paddingRight !== '' ? Math.max(0, Number(p.paddingRight) || 0) : px,
    paddingBottom:
      p.paddingBottom != null && p.paddingBottom !== '' ? Math.max(0, Number(p.paddingBottom) || 0) : py,
    paddingLeft:
      p.paddingLeft != null && p.paddingLeft !== '' ? Math.max(0, Number(p.paddingLeft) || 0) : px,
  };
}

export function detectPublicViewport(width) {
  if (width >= VIEWPORTS.desktop.minWidth) return 'desktop';
  if (width >= VIEWPORTS.tablet.minWidth) return 'tablet';
  return 'mobile';
}

export function clampColSpan(span) {
  return Math.max(1, Math.min(GRID_COLUMNS, Number(span) || 1));
}

export function clampColStart(start, span) {
  const s = clampColSpan(span);
  return Math.max(1, Math.min(GRID_COLUMNS - s + 1, Number(start) || 1));
}

/** Convertit l'ancien format x/y/w/h en grille 12 colonnes. */
export function migrateComponent(comp) {
  if (comp.colStart != null && comp.colSpan != null) {
    let withLayout = comp.layout != null ? comp : { ...comp, layout: 1 };
    if (comp.type === 'text' && comp.props) {
      withLayout = { ...withLayout, props: normalizeTextProps(comp.props) };
    }
    if (comp.type === 'menu' && comp.props) {
      withLayout = {
        ...withLayout,
        props: {
          ...comp.props,
          menuTabFontSize:
            comp.props.menuTabFontSize ??
            comp.props.menuFontSize ??
            defaultComponentProps('menu').menuTabFontSize,
          compactLogoPosition: comp.props.compactLogoPosition ?? comp.props.logoPosition ?? 'left',
          compactMenuAlign: comp.props.compactMenuAlign ?? 'right',
          mobileMenuStyle: comp.props.mobileMenuStyle ?? 'hamburger',
          overlayFirstSection: comp.props.overlayFirstSection === true,
          scrollBackgroundThreshold:
            comp.props.scrollBackgroundThreshold != null
              ? Number(comp.props.scrollBackgroundThreshold)
              : 16,
          hiddenMenuSectionIds: Array.isArray(comp.props.hiddenMenuSectionIds)
            ? comp.props.hiddenMenuSectionIds
            : [],
        },
      };
    }
    return withLayout;
  }

  const colWidth = DESKTOP_WIDTH / GRID_COLUMNS;
  const colSpan = clampColSpan(Math.round((comp.w || colWidth * 6) / colWidth));
  const colStart = clampColStart(Math.floor((comp.x || 0) / colWidth) + 1, colSpan);
  const row = Math.max(0, Math.floor((comp.y || 0) / 72));

  const { x, y, w, h, ...rest } = comp;
  const migrated = {
    ...rest,
    colStart,
    colSpan,
    row,
  };
  if (migrated.type === 'text' && migrated.props) {
    migrated.props = normalizeTextProps(migrated.props);
  }
  return migrated;
}

export function migrateSection(section) {
  const sectionType = getSectionType(section);
  let background = section.background || createSectionBackground(sectionType);
  if (sectionType === 'image' && background.type === 'image') {
    background = { ...background, imageSize: 'fill' };
  }
  return {
    ...section,
    sectionType,
    background,
    gridRows: section.gridRows ?? (sectionType === 'image' || sectionType === 'video' ? 32 : undefined),
    gridRowsCustom: section.gridRowsCustom === true,
    components: (section.components || [])
      .map(migrateComponent)
      .filter((c) => c.type !== 'colorBlock'),
  };
}

function scaleGridPlacement(comp) {
  if (!comp) return comp;
  const targetCols = V2_GRID_COLUMNS;
  const ratio = LEGACY_GRID_ROW_HEIGHT / V2_GRID_ROW_HEIGHT;
  const scaled = { ...comp };

  if (comp.colStart != null) {
    scaled.colStart = Math.min(
      targetCols,
      Math.max(1, (Number(comp.colStart) - 1) * 2 + 1),
    );
  }
  if (comp.colSpan != null) {
    scaled.colSpan = Math.min(targetCols, Math.max(1, Number(comp.colSpan) * 2));
  }
  if (comp.rowSpan != null) {
    scaled.rowSpan = Math.max(1, Math.round(Number(comp.rowSpan) * ratio));
  }

  for (const vp of ['tablet', 'mobile']) {
    const o = comp[vp];
    if (!o) continue;
    scaled[vp] = { ...o };
    if (o.colStart != null) {
      scaled[vp].colStart = Math.min(
        targetCols,
        Math.max(1, (Number(o.colStart) - 1) * 2 + 1),
      );
    }
    if (o.colSpan != null) {
      scaled[vp].colSpan = Math.min(targetCols, Math.max(1, Number(o.colSpan) * 2));
    }
    if (o.rowSpan != null) {
      scaled[vp].rowSpan = Math.max(1, Math.round(Number(o.rowSpan) * ratio));
    }
  }

  return scaled;
}

function siteUsesLegacyGrid(site) {
  const comps = [];
  if (site.pageMenu) comps.push(site.pageMenu);
  for (const sec of site.sections || []) {
    comps.push(...(sec.components || []));
  }
  if (!comps.length) return false;
  const maxSpan = Math.max(...comps.map((c) => Number(c.colSpan) || 1));
  return maxSpan <= LEGACY_GRID_COLUMNS;
}

function collectSiteGridComponents(site) {
  const comps = [];
  if (site?.pageMenu) comps.push(site.pageMenu);
  for (const sec of site?.sections || []) {
    comps.push(...(sec.components || []));
  }
  return comps;
}

/** Grille 48 cols (v3) — au moins un bloc dépasse l’ancien canevas 24 cols. */
function siteUsesV3GridCoordinates(site) {
  const comps = collectSiteGridComponents(site);
  for (const c of comps) {
    const placements = [c, c.tablet, c.mobile].filter(Boolean);
    for (const p of placements) {
      const colStart = Number(p.colStart ?? c.colStart) || 1;
      const colSpan = Number(p.colSpan ?? c.colSpan) || 1;
      if (colSpan > V2_GRID_COLUMNS || colStart > V2_GRID_COLUMNS) return true;
      if (colStart + colSpan - 1 > V2_GRID_COLUMNS) return true;
    }
  }
  return false;
}

/**
 * Vrai seulement pour d’anciens sites en grille 24 cols (v2).
 * Les sites « vanilla » v3 (petits blocs colSpan 2–8) ne doivent pas être doublés à la sauvegarde.
 */
function siteClearlyUsesV2Grid(site) {
  const comps = collectSiteGridComponents(site);
  if (!comps.length) return false;
  if (siteUsesV3GridCoordinates(site)) return false;

  let maxSpan = 1;
  let maxRight = 1;
  for (const c of comps) {
    for (const p of [c, c.tablet, c.mobile]) {
      if (!p) continue;
      const colStart = Number(p.colStart ?? c.colStart) || 1;
      const colSpan = Number(p.colSpan ?? c.colSpan) || 1;
      maxSpan = Math.max(maxSpan, colSpan);
      maxRight = Math.max(maxRight, colStart + colSpan - 1);
    }
  }
  if (maxSpan >= V2_GRID_COLUMNS) return true;
  if (maxSpan >= 12 && maxRight <= V2_GRID_COLUMNS) return true;
  return false;
}

function migrateV2ToV3(site) {
  const scale = (comp) => {
    if (!comp) return comp;
    const scaled = { ...comp };
    if (comp.colStart != null) {
      scaled.colStart = Math.min(
        GRID_COLUMNS,
        Math.max(1, (Number(comp.colStart) - 1) * 2 + 1),
      );
    }
    if (comp.colSpan != null) {
      scaled.colSpan = Math.min(GRID_COLUMNS, Math.max(1, Number(comp.colSpan) * 2));
    }
    if (comp.rowSpan != null) {
      scaled.rowSpan = Math.max(1, Number(comp.rowSpan) * 2);
    }
    for (const vp of ['tablet', 'mobile']) {
      const o = comp[vp];
      if (!o) continue;
      scaled[vp] = { ...o };
      if (o.colStart != null) {
        scaled[vp].colStart = Math.min(
          GRID_COLUMNS,
          Math.max(1, (Number(o.colStart) - 1) * 2 + 1),
        );
      }
      if (o.colSpan != null) {
        scaled[vp].colSpan = Math.min(GRID_COLUMNS, Math.max(1, Number(o.colSpan) * 2));
      }
      if (o.rowSpan != null) {
        scaled[vp].rowSpan = Math.max(1, Number(o.rowSpan) * 2);
      }
    }
    return scaled;
  };

  return {
    ...site,
    gridVersion: 3,
    pageMenu: site.pageMenu ? scale(migrateComponent(site.pageMenu)) : null,
    sections: (site.sections || []).map((sec) => ({
      ...sec,
      gridRows: sec.gridRows != null ? Math.max(1, Number(sec.gridRows) * 2) : sec.gridRows,
      components: (sec.components || []).map((c) => scale(migrateComponent(c))),
    })),
  };
}

function migrateGridSite(site) {
  if (!site) return site;
  let next = site;
  if ((next.gridVersion || 0) < 2) {
    if (!siteUsesLegacyGrid(next)) {
      next = { ...next, gridVersion: 2 };
    } else {
      next = {
        ...next,
        gridVersion: 2,
        pageMenu: next.pageMenu
          ? scaleGridPlacement(migrateComponent(next.pageMenu))
          : null,
        sections: (next.sections || []).map((sec) => ({
          ...sec,
          components: (sec.components || []).map((c) =>
            scaleGridPlacement(migrateComponent(c)),
          ),
        })),
      };
    }
  }
  if ((next.gridVersion || 0) < 3) {
    if (siteClearlyUsesV2Grid(next)) {
      next = migrateV2ToV3(next);
    } else {
      next = { ...next, gridVersion: 3 };
    }
  }
  return next;
}

export function migrateSite(site) {
  if (!site) return site;
  const migrated = migrateGridSite({
    ...site,
    description: site.description || '',
    seo: { ...DEFAULT_SEO, ...(site.seo || {}) },
    geo: { ...DEFAULT_GEO, ...(site.geo || {}) },
    typography: normalizeSiteTypography({ ...DEFAULT_TYPOGRAPHY, ...(site.typography || {}) }),
    ambientMusic: normalizeAmbientMusic(site.ambientMusic),
    pageMenu: site.pageMenu ? migrateComponent(site.pageMenu) : null,
    sections: (site.sections || []).map(migrateSection),
  });
  const stripped = stripPlaceholderSection(migrated);
  const restaurant = migrateRestaurantSiteSections(stripped);
  const repaired = stripDuplicateJardinsPageMenu(repairJardinsKitSectionMeta(restaurant));
  if (Array.isArray(repaired.reservationRequests)) {
    return {
      ...repaired,
      reservationDayBookings: buildReservationDayBookingsFromRequests(repaired.reservationRequests),
    };
  }
  return repaired;
}

/** Anciens sites avec une section « Accueil » vide créée par défaut. */
function stripPlaceholderSection(site) {
  const sections = site.sections || [];
  if (sections.length !== 1) return site;
  const [sec] = sections;
  const empty = !(sec.components || []).length;
  const isDefault =
    sec.label === 'Accueil' ||
    sec.anchor === 'accueil' ||
    sec.label === 'Section 1';
  if (empty && isDefault) {
    return { ...site, sections: [] };
  }
  return site;
}

function autoGridPlacement(base, viewport) {
  if (viewport === 'desktop') return { ...base };
  if (viewport === 'mobile') {
    return { colStart: 1, colSpan: GRID_COLUMNS, row: base.row };
  }
  // tablette : colonnes larges passent en pleine largeur
  if (base.colSpan > GRID_COLUMNS / 2) {
    return { colStart: 1, colSpan: GRID_COLUMNS, row: base.row };
  }
  return { ...base };
}

/** Placement grille : desktop = base ; tablette/tél. = auto + overrides partiels. */
export function getGridPlacement(comp, viewport = 'desktop') {
  const migrated = migrateComponent(comp);
  const base = {
    colStart: clampColStart(migrated.colStart ?? 1, migrated.colSpan ?? GRID_COLUMNS),
    colSpan: clampColSpan(migrated.colSpan ?? GRID_COLUMNS),
    row: Math.max(0, Number(migrated.row) || 0),
    rowSpan: Math.max(1, Number(migrated.rowSpan) || 1),
  };

  if (viewport === 'desktop') return base;

  const auto = autoGridPlacement(base, viewport);
  const override = migrated[viewport];
  if (!override) return auto;

  const colSpan = override.colSpan != null ? clampColSpan(override.colSpan) : auto.colSpan;
  return {
    colStart:
      override.colStart != null
        ? clampColStart(override.colStart, colSpan)
        : auto.colStart,
    colSpan,
    row: override.row != null ? Math.max(0, Number(override.row)) : auto.row,
    rowSpan:
      override.rowSpan != null
        ? Math.max(1, Number(override.rowSpan))
        : auto.rowSpan,
  };
}

export function hasCustomViewportLayout(comp, viewport) {
  if (viewport === 'desktop') return false;
  const o = migrateComponent(comp)[viewport];
  return o != null && (o.colStart != null || o.colSpan != null || o.row != null || o.rowSpan != null);
}

export function gridStyle(placement, interacting = false) {
  const rowSpan = placement.rowSpan ?? 1;
  return {
    gridColumn: `${placement.colStart} / span ${placement.colSpan}`,
    gridRow: `${placement.row + 1} / span ${rowSpan}`,
    transition: interacting ? 'none' : 'grid-column 0.22s ease, grid-row 0.22s ease',
  };
}

export function nextAvailableRow(components) {
  if (!components?.length) return 0;
  return Math.max(...components.map((c) => getGridPlacement(c, 'desktop').row)) + 1;
}

/** Prochain emplacement libre sur la grille 12 colonnes. */
export function nextAvailablePlacement(components, colSpan = 1) {
  const span = clampColSpan(colSpan);
  if (!components?.length) return { colStart: 1, colSpan: span, row: 0 };

  const placed = components.map((c) => getGridPlacement(c, 'desktop'));

  const overlaps = (row, colStart) =>
    placed.some(
      (p) =>
        p.row === row &&
        !(colStart + span - 1 < p.colStart || colStart > p.colStart + p.colSpan - 1),
    );

  for (let row = 0; row <= 80; row += 1) {
    for (let col = 1; col <= GRID_COLUMNS - span + 1; col += 1) {
      if (!overlaps(row, col)) return { colStart: col, colSpan: span, row, rowSpan: 1 };
    }
  }

  return { colStart: 1, colSpan: span, row: nextAvailableRow(components), rowSpan: 1 };
}

export function siteHasMenu(site) {
  if (site?.pageMenu) return true;
  return (site?.sections || []).some((sec) =>
    (sec.components || []).some((c) => c.type === 'menu'),
  );
}

export function defaultGridForType(type) {
  switch (type) {
    case 'menu':
      return { colStart: 1, colSpan: GRID_COLUMNS, row: 0, rowSpan: 2 };
    case 'restaurantPage':
      return { colStart: 1, colSpan: GRID_COLUMNS, row: 0, rowSpan: 80 };
    case 'restaurantSection':
      return { colStart: 1, colSpan: GRID_COLUMNS, row: 0, rowSpan: 10 };
    case 'hubupQr':
      return { colStart: 1, colSpan: 12, row: 0, rowSpan: 12 };
    case 'hubupNotifications':
      return { colStart: 1, colSpan: 24, row: 0, rowSpan: 18 };
    case 'icon':
      return { colStart: 1, colSpan: 4, row: 0, rowSpan: 4 };
    case 'product':
      return { colStart: 1, colSpan: 16, row: 0, rowSpan: 36 };
    case 'dropdown':
      return { colStart: 1, colSpan: 16, row: 0, rowSpan: 4 };
    case 'divider':
      return { colStart: 1, colSpan: GRID_COLUMNS, row: 0, rowSpan: 2 };
    case 'badge':
      return { colStart: 1, colSpan: 8, row: 0, rowSpan: 2 };
    case 'quote':
      return { colStart: 1, colSpan: 20, row: 0, rowSpan: 14 };
    case 'cardFeature':
      return { colStart: 1, colSpan: 14, row: 0, rowSpan: 18 };
    case 'cardIllustration':
      return { colStart: 1, colSpan: 12, row: 0, rowSpan: 22 };
    case 'cardSplit':
      return { colStart: 1, colSpan: GRID_COLUMNS, row: 0, rowSpan: 20 };
    default:
      return { colStart: 1, colSpan: 2, row: 0, rowSpan: 2 };
  }
}

export const COMPONENT_GRID_MIN = {
  hubupQr: { colSpan: 8, rowSpan: 10 },
  hubupNotifications: { colSpan: 12, rowSpan: 14 },
  cardFeature: { colSpan: 8, rowSpan: 12 },
  cardIllustration: { colSpan: 8, rowSpan: 14 },
  cardSplit: { colSpan: 16, rowSpan: 14 },
};

export function getComponentGridMin(type) {
  return COMPONENT_GRID_MIN[type] || { colSpan: 1, rowSpan: 1 };
}

export const COMPONENT_CATALOG = [
  {
    type: 'section-normal',
    label: 'Section couleur',
    icon: 'Square',
    description: 'Bloc avec fond couleur ou dégradé',
  },
  {
    type: 'section-image',
    label: 'Section image',
    icon: 'Image',
    description: 'Fond image ajustée à la section (sans rognage)',
  },
  {
    type: 'section-video',
    label: 'Section vidéo',
    icon: 'Video',
    description: 'Fond vidéo en boucle',
  },
  {
    type: 'text',
    label: 'Texte',
    icon: 'Type',
    description: 'Titre ou paragraphe',
  },
  {
    type: 'image',
    label: 'Image',
    icon: 'Image',
    description: 'Photo ou visuel',
  },
  {
    type: 'button',
    label: 'Bouton',
    icon: 'MousePointerClick',
    description: 'Lien vers une section ou URL',
  },
  {
    type: 'dropdown',
    label: 'Menu déroulant',
    icon: 'ChevronDown',
    description: 'Liste déroulante avec liens section ou URL',
  },
  {
    type: 'icon',
    label: 'Icône',
    icon: 'Sparkles',
    description: 'Icône vectorielle personnalisable',
  },
  {
    type: 'divider',
    label: 'Séparateur',
    icon: 'Minus',
    description: 'Ligne de séparation horizontale, avec ou sans texte',
  },
  {
    type: 'badge',
    label: 'Badge',
    icon: 'Tag',
    description: 'Étiquette ou pastille (nouveau, promo, catégorie…)',
  },
  {
    type: 'quote',
    label: 'Citation',
    icon: 'MessageCircle',
    description: 'Bloc témoignage ou citation avec auteur',
  },
  {
    type: 'product',
    label: 'Produit',
    icon: 'ShoppingBag',
    description: 'Carte produit avec achat Stancer (campagne)',
  },
  {
    type: 'cardFeature',
    label: 'Carte — icônes & lien',
    icon: 'LayoutGrid',
    description: 'Pictos, titre, description et lien avec flèche animée',
  },
  {
    type: 'cardIllustration',
    label: 'Carte — illustration',
    icon: 'Image',
    description: 'Image pleine largeur au-dessus du texte',
  },
  {
    type: 'cardSplit',
    label: 'Carte — texte & image',
    icon: 'LayoutTemplate',
    description: 'Deux colonnes : contenu + bouton à gauche, visuel à droite',
  },
  {
    type: 'restaurantSection',
    label: 'Restaurant — bloc de page',
    icon: 'UtensilsCrossed',
    description: 'Section du modèle La Table des Jardins (hero, galerie, réservation…)',
  },
  {
    type: 'clickAndCollect',
    label: 'Click & collect',
    icon: 'ShoppingCart',
    description: 'Carte restaurant et panier (page dédiée ou intégré)',
  },
  {
    type: 'reservation',
    label: 'Réservation',
    icon: 'CalendarCheck',
    description: 'Formulaire de réservation intégré sur la page',
  },
  {
    type: 'menu',
    label: 'Menu',
    icon: 'Menu',
    description: 'Navigation avec logo et onglets',
  },
  {
    type: 'hubupQr',
    label: 'QR Code HubUP',
    icon: 'QrCode',
    description: 'QR d’invitation vers une communauté HubUP',
  },
  {
    type: 'hubupNotifications',
    label: 'Notifications HubUP',
    icon: 'Bell',
    description: 'Dernières annonces d’une communauté / rubrique',
  },
];

export function defaultComponentProps(type) {
  const base = {
    componentBackground: { ...DEFAULT_COMPONENT_BACKGROUND },
    paddingX: 0,
    paddingY: 0,
  };
  switch (type) {
    case 'text':
      return {
        ...base,
        content: DEFAULT_TEXT_HTML,
        contentFormat: 'html',
        inheritSiteFont: true,
        paddingX: 16,
        paddingY: 8,
        contentHoverEffect: 'none',
        contentHoverIntensity: 65,
        textFontFamily: 'system',
        letterSpacing: 0,
      };
    case 'image':
      return {
        ...base,
        src: '',
        objectFit: 'fill',
        objectPosition: 'center',
        imageZoom: 100,
        borderRadius: 0,
        shape: 'square',
        minHeight: 200,
        paddingX: 0,
        paddingY: 0,
        imageHoverEffect: 'none',
        imageHoverIntensity: 65,
      };
    case 'button':
      return {
        ...base,
        label: 'En savoir plus',
        linkType: 'section',
        sectionAnchor: 'accueil',
        url: '',
        backgroundColor: '#2563eb',
        textColor: '#ffffff',
        shape: 'rounded',
        borderRadius: 8,
        width: 'auto',
        borderWidth: 0,
        borderColor: 'transparent',
        fontSize: 14,
        fontWeight: 500,
        inheritSiteButtonFont: true,
        buttonFontFamily: 'system',
        buttonHoverEffect: 'none',
        buttonHoverIntensity: 65,
      };
    case 'clickAndCollect':
      return {
        ...base,
        displayMode: 'inline',
        label: 'Click & collect',
        backgroundColor: '#92400e',
        textColor: '#ffffff',
        shape: 'rounded',
        borderRadius: 8,
        width: 'auto',
        borderWidth: 0,
        borderColor: 'transparent',
        fontSize: 14,
        fontWeight: 500,
        inheritSiteButtonFont: true,
        buttonFontFamily: 'system',
        paddingX: 0,
        paddingY: 0,
        menuPage: {
          pageSubtitle: 'vous propose',
          layout: 'classic',
          accentColor: '#432728',
          backgroundColor: '#eae7e3',
          orderButtonColor: '#432728',
          orderButtonTextColor: '#ffffff',
          menuTheme: {
            showOrnaments: true,
            showFrame: true,
            inkColor: '#432728',
            pageBackground: '#eae7e3',
          },
        },
      };
    case 'reservation':
      return {
        ...base,
        displayMode: 'inline',
        theme: 'default',
        label: 'Réserver',
        backgroundColor: '#0f766e',
        textColor: '#ffffff',
        shape: 'rounded',
        borderRadius: 8,
        width: 'auto',
        borderWidth: 0,
        borderColor: 'transparent',
        fontSize: 14,
        fontWeight: 500,
        inheritSiteButtonFont: true,
        buttonFontFamily: 'system',
        paddingX: 0,
        paddingY: 0,
        reservationPage: {
          formTitle: 'Votre table, en quelques clics.',
          formSubtitle: '',
          submitButtonLabel: 'Demander ma réservation',
          submitButtonLabelBusy: 'Envoi…',
          accentColor: '#203b2d',
          backgroundColor: '#f8fafc',
          buttonColor: '#203b2d',
          buttonTextColor: '#ffffff',
        },
      };
    case 'dropdown':
      return {
        ...base,
        placeholder: 'Sélectionner une option…',
        options: [
          {
            id: uid('opt'),
            label: 'Accueil',
            linkType: 'section',
            sectionAnchor: 'accueil',
            url: '',
          },
          {
            id: uid('opt'),
            label: 'En savoir plus',
            linkType: 'section',
            sectionAnchor: 'accueil',
            url: '',
          },
        ],
        width: 'full',
        backgroundColor: { mode: 'solid', color: '#ffffff', alpha: 1 },
        textColor: { mode: 'solid', color: '#1c1917', alpha: 1 },
        borderColor: { mode: 'solid', color: '#e5e7eb', alpha: 1 },
        menuBackgroundColor: { mode: 'solid', color: '#ffffff', alpha: 1 },
        menuTextColor: { mode: 'solid', color: '#1c1917', alpha: 1 },
        shape: 'rounded',
        borderRadius: 8,
        borderWidth: 1,
        fontSize: 14,
        fontWeight: 500,
        inheritSiteButtonFont: true,
        buttonFontFamily: 'system',
        paddingX: 12,
        paddingY: 10,
      };
    case 'divider':
      return {
        ...base,
        label: '',
        lineColor: { mode: 'solid', color: '#e5e7eb', alpha: 1 },
        lineStyle: 'solid',
        thickness: 1,
        labelColor: { mode: 'solid', color: '#64748b', alpha: 1 },
        width: 'full',
        paddingX: 16,
        paddingY: 8,
      };
    case 'badge':
      return {
        ...base,
        text: 'Nouveau',
        backgroundColor: { mode: 'solid', color: '#2563eb', alpha: 1 },
        textColor: { mode: 'solid', color: '#ffffff', alpha: 1 },
        shape: 'pill',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600,
        align: 'center',
        paddingX: 8,
        paddingY: 8,
      };
    case 'quote':
      return {
        ...base,
        quote: 'Un service exceptionnel — rapide, fiable et une équipe à l\'écoute.',
        authorName: 'Marie Dupont',
        authorRole: 'Directrice marketing',
        avatarUrl: '',
        accentColor: { mode: 'solid', color: '#2563eb', alpha: 1 },
        showQuoteMark: true,
        cardStyle: true,
        align: 'left',
        paddingX: 20,
        paddingY: 16,
      };
    case 'icon':
      return {
        ...base,
        iconName: 'Star',
        size: 32,
        color: '#2563eb',
        strokeWidth: 2,
        align: 'center',
      };
    case 'product':
      return {
        ...base,
        campaignId: '',
        productId: '',
        inheritTitle: true,
        inheritDescription: true,
        productName: '',
        productDescription: '',
        title: '',
        description: '',
        imageUrl: '',
        price: null,
        stancerConfigured: false,
        buttonLabel: 'Acheter',
        shape: 'rounded',
        borderRadius: 12,
      };
    case 'cardFeature':
      return {
        ...base,
        componentBackground: {
          enabled: true,
          type: 'color',
          color: '#ffffff',
          fill: { mode: 'solid', color: '#ffffff', alpha: 1 },
          imageUrl: '',
          imageSize: 'cover',
          imagePosition: 'center',
        },
        leftMediaType: 'icon',
        leftIconName: 'Sparkles',
        leftIconColor: '#2563eb',
        leftIconSize: 28,
        leftImageUrl: '',
        rightMediaType: 'icon',
        rightIconName: 'ExternalLink',
        rightIconColor: '#94a3b8',
        rightIconSize: 24,
        rightImageUrl: '',
        title: 'Titre de la carte',
        titleColor: { mode: 'solid', color: '#0f172a', alpha: 1 },
        titleFontSize: 18,
        titleFontWeight: 600,
        description: 'Une description courte pour mettre en avant votre proposition.',
        descriptionColor: { mode: 'solid', color: '#64748b', alpha: 1 },
        descriptionFontSize: 14,
        linkLabel: 'En savoir plus',
        linkColor: { mode: 'solid', color: '#2563eb', alpha: 1 },
        linkHoverColor: { mode: 'solid', color: '#1d4ed8', alpha: 1 },
        linkHoverEffect: 'none',
        linkHoverIntensity: 65,
        cardHoverEffect: 'none',
        cardHoverIntensity: 65,
        linkType: 'section',
        sectionAnchor: '',
        url: '',
        publicPath: '',
        shape: 'rounded',
        borderRadius: 12,
        paddingX: 0,
        paddingY: 0,
      };
    case 'cardIllustration':
      return {
        ...base,
        componentBackground: {
          enabled: true,
          type: 'color',
          color: '#ffffff',
          fill: { mode: 'solid', color: '#ffffff', alpha: 1 },
          imageUrl: '',
          imageSize: 'cover',
          imagePosition: 'center',
        },
        imageUrl: '',
        imageAlt: '',
        imageObjectFit: 'cover',
        imageHoverEffect: 'none',
        imageHoverIntensity: 65,
        cardHoverEffect: 'none',
        cardHoverIntensity: 65,
        title: 'Titre',
        titleColor: { mode: 'solid', color: '#0f172a', alpha: 1 },
        titleFontSize: 18,
        titleFontWeight: 600,
        description: 'Texte descriptif sous l’illustration.',
        descriptionColor: { mode: 'solid', color: '#64748b', alpha: 1 },
        descriptionFontSize: 14,
        shape: 'rounded',
        borderRadius: 12,
        paddingX: 0,
        paddingY: 0,
      };
    case 'cardSplit':
      return {
        ...base,
        componentBackground: {
          enabled: true,
          type: 'color',
          color: '#ffffff',
          fill: { mode: 'solid', color: '#ffffff', alpha: 1 },
          imageUrl: '',
          imageSize: 'cover',
          imagePosition: 'center',
        },
        eyebrow: 'Catégorie',
        eyebrowColor: { mode: 'solid', color: '#64748b', alpha: 1 },
        subtitle: 'Sous-titre accrocheur',
        subtitleColor: { mode: 'solid', color: '#475569', alpha: 1 },
        heading: 'Titre principal',
        headingColor: { mode: 'solid', color: '#0f172a', alpha: 1 },
        headingFontSize: 24,
        headingFontWeight: 700,
        description: 'Paragraphe de description. Le bouton ci-dessous suit la largeur du contenu.',
        descriptionColor: { mode: 'solid', color: '#64748b', alpha: 1 },
        imageUrl: '',
        imageAlt: '',
        buttonLabel: 'Découvrir',
        buttonBackgroundColor: { mode: 'solid', color: '#2563eb', alpha: 1 },
        buttonTextColor: { mode: 'solid', color: '#ffffff', alpha: 1 },
        buttonShape: 'rounded',
        buttonBorderRadius: 8,
        buttonFontSize: 14,
        buttonFontWeight: 600,
        buttonLinkType: 'section',
        buttonSectionAnchor: '',
        buttonUrl: '',
        buttonPublicPath: '',
        inheritSiteButtonFont: true,
        buttonFontFamily: 'system',
        buttonHoverEffect: 'none',
        buttonHoverIntensity: 65,
        imageHoverEffect: 'none',
        imageHoverIntensity: 65,
        cardHoverEffect: 'none',
        cardHoverIntensity: 65,
        shape: 'rounded',
        borderRadius: 12,
        paddingX: 0,
        paddingY: 0,
      };
    case 'menu':
      return {
        ...base,
        logoUrl: '',
        logoPosition: 'left',
        tabsPosition: 'right',
        backgroundColor: '#ffffff',
        textColor: '#111827',
        paddingX: 32,
        paddingY: 16,
        compactLogoPosition: 'left',
        compactMenuAlign: 'right',
        mobileMenuStyle: 'hamburger',
        overlayFirstSection: false,
        scrollBackgroundColor: null,
        scrollBackgroundThreshold: 16,
        hiddenMenuSectionIds: [],
        sectionOrder: [],
        externalLinks: [],
        menuButtons: [],
        tabOrder: [],
        menuFreeLayout: false,
        tabPlacements: {},
        logoMenuPlacement: null,
        inheritSiteMenuFont: true,
        menuFontFamily: 'system',
        menuFontSize: 14,
        menuTabFontSize: 14,
        tabHoverEffects: {},
        tabAppearances: {},
        menuTabDefaultVariant: 'transparent',
        menuTabShape: 'rounded',
        menuTabBorderRadius: 8,
        menuTabSolidBackground: { mode: 'solid', color: '#ffffff', alpha: 1 },
        menuTabSolidTextColor: null,
      };
    case 'hubupQr':
      return {
        ...base,
        groupId: '',
        communityName: '',
        inviteCode: '',
        inviteUrl: '',
        groupIconUrl: '',
        isPro: false,
        showLabel: true,
        paddingX: 12,
        paddingY: 12,
      };
    case 'restaurantPage':
      return {
        ...base,
        ...DEFAULT_RESTAURANT_EXEMPLE_PROPS,
        paddingX: 0,
        paddingY: 0,
      };
    case 'restaurantSection':
      return {
        ...base,
        ...defaultPropsForRestaurantVariant('hero'),
      };
    case 'hubupNotifications':
      return {
        ...base,
        groupId: '',
        topicId: '',
        communityName: '',
        inviteCode: '',
        groupIconUrl: '',
        isPro: false,
        notificationCount: 3,
        cachedNotifications: [],
        fetchedAt: null,
        paddingX: 12,
        paddingY: 12,
      };
    default:
      return base;
  }
}

export function getComponentLayout(comp) {
  const n = Number(comp?.layout);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

export function gridPlacementsOverlap(pa, pb) {
  const aColEnd = pa.colStart + (pa.colSpan ?? 1) - 1;
  const bColEnd = pb.colStart + (pb.colSpan ?? 1) - 1;
  const aRowEnd = pa.row + (pa.rowSpan ?? 1) - 1;
  const bRowEnd = pb.row + (pb.rowSpan ?? 1) - 1;
  return !(
    pa.colStart > bColEnd ||
    pb.colStart > aColEnd ||
    pa.row > bRowEnd ||
    pb.row > aRowEnd
  );
}

export function componentOverlapsOthers(comp, components, viewport = 'desktop') {
  const pa = getGridPlacement(comp, viewport);
  return (components || []).some((other) => {
    if (other.id === comp.id || isComponentHidden(other, viewport)) return false;
    return gridPlacementsOverlap(pa, getGridPlacement(other, viewport));
  });
}

export function normalizeSectionStackOrder(components) {
  const sorted = [...(components || [])].sort((a, b) => {
    const diff = getComponentLayout(a) - getComponentLayout(b);
    if (diff !== 0) return diff;
    return String(a.id).localeCompare(String(b.id));
  });
  return sorted.map((c, index) => ({ ...c, layout: index + 1 }));
}

export function bringComponentToFront(components, compId) {
  const max = Math.max(1, ...(components || []).map(getComponentLayout));
  const next = (components || []).map((c) =>
    c.id === compId ? { ...c, layout: max + 1 } : c,
  );
  return normalizeSectionStackOrder(next);
}

export function sendComponentToBack(components, compId) {
  const next = (components || []).map((c) =>
    c.id === compId ? { ...c, layout: 0 } : c,
  );
  return normalizeSectionStackOrder(next);
}

/** Premier plan vs second plan (relatif à la section). */
export const LAYER_PLANE = {
  FOREGROUND: 'foreground',
  BACKGROUND: 'background',
};

/** all = tout · foreground = premier plan seul · background = second plan seul */
export const DEFAULT_PLANE_FILTER = {
  mode: 'all',
};

export function getSectionLayoutRange(components) {
  const layouts = (components || []).map(getComponentLayout);
  if (!layouts.length) return { min: 1, max: 1 };
  return { min: Math.min(...layouts), max: Math.max(...layouts) };
}

/** Un composant est au-dessus d'un autre (calque ou ordre d'empilement). */
export function isComponentStackedAbove(upper, lower, sectionComponents, viewport = 'desktop') {
  const upperLayout = getComponentLayout(upper);
  const lowerLayout = getComponentLayout(lower);
  if (upperLayout !== lowerLayout) return upperLayout > lowerLayout;
  const sorted = sortComponentsByGrid(sectionComponents || [], viewport);
  const upperIdx = sorted.findIndex((c) => c.id === upper.id);
  const lowerIdx = sorted.findIndex((c) => c.id === lower.id);
  return upperIdx > lowerIdx;
}

/** Recouvrement partiel ou total avec un composant devant. */
export function isComponentPartiallyCovered(comp, sectionComponents, viewport = 'desktop') {
  const components = (sectionComponents || []).filter(
    (c) => c.id !== comp.id && !isComponentHidden(c, viewport),
  );
  const placement = getGridPlacement(comp, viewport);
  return components.some((other) => {
    if (!isComponentStackedAbove(other, comp, sectionComponents, viewport)) return false;
    return gridPlacementsOverlap(placement, getGridPlacement(other, viewport));
  });
}

export function getComponentPlane(comp, sectionComponents, viewport = 'desktop') {
  const components = (sectionComponents || []).filter(
    (c) => !isComponentHidden(c, viewport),
  );
  const layer = getComponentLayout(comp);
  const { min, max } = getSectionLayoutRange(components);

  if (!components.length) return LAYER_PLANE.FOREGROUND;

  // Recouvert (même partiellement) par un élément devant → second plan
  if (isComponentPartiallyCovered(comp, components, viewport)) {
    return LAYER_PLANE.BACKGROUND;
  }

  // Recouvre un autre élément → premier plan
  const placement = getGridPlacement(comp, viewport);
  const coversOther = components.some((other) => {
    if (other.id === comp.id) return false;
    if (!isComponentStackedAbove(comp, other, components, viewport)) return false;
    return gridPlacementsOverlap(placement, getGridPlacement(other, viewport));
  });
  if (coversOther) return LAYER_PLANE.FOREGROUND;

  // Sans chevauchement : règles de calque
  if (max > 1 && layer <= 1) return LAYER_PLANE.BACKGROUND;
  if (min < max && layer <= min) return LAYER_PLANE.BACKGROUND;
  if (min === max && comp.type === 'image') return LAYER_PLANE.BACKGROUND;

  return LAYER_PLANE.FOREGROUND;
}

export function isComponentPlaneActive(
  comp,
  planeFilter = DEFAULT_PLANE_FILTER,
  sectionComponents,
  viewport = 'desktop',
) {
  const mode = planeFilter?.mode || 'all';
  if (mode === 'all') return true;
  const plane = getComponentPlane(comp, sectionComponents, viewport);
  return plane === mode;
}

export function isPageMenuPlaneActive(planeFilter = DEFAULT_PLANE_FILTER) {
  const mode = planeFilter?.mode || 'all';
  return mode !== 'background';
}

/**
 * Style éditeur : garde l'ordre z naturel, le plan inactif laisse passer les clics (recouvrement partiel OK).
 */
export function getEditorPlaneVisual(
  comp,
  planeFilter,
  sectionComponents,
  { selected = false, interacting = false, viewport = 'desktop' } = {},
) {
  const mode = planeFilter?.mode || 'all';
  const active =
    mode === 'all' ||
    isComponentPlaneActive(comp, planeFilter, sectionComponents, viewport);
  const stack = componentStackStyle(comp, {
    selected: selected && active,
    interacting: interacting && active,
    editor: true,
  });

  if (mode === 'all') {
    return { planeInactive: false, ...stack };
  }

  return {
    planeInactive: !active,
    opacity: active ? 1 : 0.12,
    pointerEvents: active ? 'auto' : 'none',
    ...stack,
  };
}

export function getEditorPageMenuVisual(planeFilter, { selected = false } = {}) {
  const mode = planeFilter?.mode || 'all';
  const active = isPageMenuPlaneActive(planeFilter);
  const zIndex =
    selected && active ? EDITOR_Z_INDEX.menuSelected : EDITOR_Z_INDEX.menu;
  const base = {
    position: 'sticky',
    top: 0,
    zIndex,
  };

  if (mode === 'all') {
    return { planeInactive: false, ...base };
  }

  return {
    planeInactive: !active,
    opacity: active ? 1 : 0.12,
    pointerEvents: active ? 'auto' : 'none',
    ...base,
  };
}

export function nudgeComponentLayer(components, compId, delta) {
  const sorted = sortComponentsByLayer(components || []);
  const idx = sorted.findIndex((c) => c.id === compId);
  if (idx < 0) return components;
  const swapIdx = delta > 0 ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= sorted.length) return components;
  const currentLayout = getComponentLayout(sorted[idx]);
  const swapLayout = getComponentLayout(sorted[swapIdx]);
  return (components || []).map((c) => {
    if (c.id === sorted[idx].id) return { ...c, layout: swapLayout };
    if (c.id === sorted[swapIdx].id) return { ...c, layout: currentLayout };
    return c;
  });
}

/** Masqué globalement ou sur un viewport (desktop / tablet / mobile). */
export function isComponentHidden(comp, viewport = 'desktop') {
  if (!comp) return false;
  if (comp.hidden === true) return true;
  if (Array.isArray(comp.viewports) && comp.viewports.length > 0 && !comp.viewports.includes(viewport)) {
    return true;
  }
  if (viewport !== 'desktop' && comp[viewport]?.hidden === true) return true;
  return false;
}

/** Section entière masquée sur tablette / téléphone (ou globalement sur desktop). */
export function isSectionHidden(section, viewport = 'desktop') {
  if (!section) return false;
  if (viewport === 'desktop' && section.hidden === true) return true;
  if (viewport !== 'desktop' && section[viewport]?.hidden === true) return true;
  return false;
}

/** Viewports où le composant est affiché (tous si non restreint). */
export function getComponentViewports(comp) {
  if (Array.isArray(comp?.viewports) && comp.viewports.length > 0) {
    return comp.viewports.filter((id) => VIEWPORT_IDS.includes(id));
  }
  return [...VIEWPORT_IDS];
}

/** Restreint ou libère l'affichage multi-écrans. */
export function setComponentViewports(comp, viewports) {
  if (!comp) return comp;
  const normalized = (viewports || []).filter((id) => VIEWPORT_IDS.includes(id));
  if (normalized.length === 0 || normalized.length >= VIEWPORT_IDS.length) {
    const { viewports: _drop, ...rest } = comp;
    return rest;
  }
  return { ...comp, viewports: [...new Set(normalized)].sort() };
}

export function hasViewportAnimationOverride(comp, viewport) {
  if (!comp || viewport === 'desktop') return false;
  return comp[viewport]?.animation != null;
}

export function getViewportAnimationSource(comp, viewport = 'desktop') {
  if (viewport !== 'desktop' && comp?.[viewport]?.animation != null) {
    return comp[viewport].animation;
  }
  return comp?.props?.animation;
}

export function setViewportAnimation(comp, viewport, animation) {
  if (!comp) return comp;
  if (viewport === 'desktop') {
    return { ...comp, props: { ...(comp.props || {}), animation } };
  }
  return {
    ...comp,
    [viewport]: { ...(comp[viewport] || {}), animation },
  };
}

export function clearViewportAnimationOverride(comp, viewport) {
  if (!comp || viewport === 'desktop') return comp;
  const next = { ...(comp[viewport] || {}) };
  delete next.animation;
  if (Object.keys(next).length === 0) {
    const { [viewport]: _drop, ...rest } = comp;
    return rest;
  }
  return { ...comp, [viewport]: next };
}

export function getComponentTypeMeta(type) {
  return COMPONENT_CATALOG.find((c) => c.type === type) || { label: type, icon: 'Square' };
}

export function getComponentDisplayName(comp) {
  const meta = getComponentTypeMeta(comp?.type);
  const p = comp?.props || {};
  if (comp?.type === 'text') {
    const plain = String(p.content || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (plain) {
      const slice = plain.slice(0, 44);
      return slice + (plain.length > 44 ? '…' : '');
    }
  }
  if (comp?.type === 'button' && p.label) return String(p.label);
  if (comp?.type === 'dropdown') {
    if (p.placeholder) return String(p.placeholder);
    const first = (p.options || [])[0];
    if (first?.label) return String(first.label);
  }
  if (comp?.type === 'product' && p.title) return String(p.title);
  if (comp?.type === 'badge' && p.text) return String(p.text);
  if (comp?.type === 'quote' && p.quote) {
    const q = String(p.quote).slice(0, 44);
    return q + (String(p.quote).length > 44 ? '…' : '');
  }
  if (comp?.type === 'divider') return p.label ? String(p.label) : 'Séparateur';
  if (comp?.type === 'icon' && p.iconName) return `Icône · ${p.iconName}`;
  return meta.label;
}

/** Tri par calque décroissant (calques supérieurs en premier). */
export function sortComponentsByLayer(components) {
  return [...(components || [])].sort((a, b) => {
    const la = getComponentLayout(a);
    const lb = getComponentLayout(b);
    if (la !== lb) return lb - la;
    const pa = getGridPlacement(a, 'desktop');
    const pb = getGridPlacement(b, 'desktop');
    if (pa.row !== pb.row) return pa.row - pb.row;
    return pa.colStart - pb.colStart;
  });
}

export function cloneComponent(comp, { placement, layoutOffset = 0 } = {}) {
  const base = getGridPlacement(comp, 'desktop');
  const nextPlacement = placement || {
    colStart: base.colStart,
    colSpan: base.colSpan,
    row: base.row,
    rowSpan: base.rowSpan ?? 1,
  };
  const { id: _id, tablet, mobile, ...rest } = comp;
  return {
    ...JSON.parse(JSON.stringify(rest)),
    id: uid('cmp'),
    colStart: nextPlacement.colStart,
    colSpan: nextPlacement.colSpan,
    row: nextPlacement.row,
    rowSpan: nextPlacement.rowSpan ?? 1,
    layout: getComponentLayout(comp) + layoutOffset,
    ...(tablet ? { tablet: JSON.parse(JSON.stringify(tablet)) } : {}),
    ...(mobile ? { mobile: JSON.parse(JSON.stringify(mobile)) } : {}),
  };
}

export function duplicateComponent(comp) {
  const base = getGridPlacement(comp, 'desktop');
  return cloneComponent(comp, {
    placement: {
      ...base,
      row: base.row + (base.rowSpan ?? 1),
    },
  });
}

export function createComponent(type, existingComponents = [], { viewports } = {}) {
  const defaults = defaultGridForType(type);
  const grid = nextAvailablePlacement(existingComponents, defaults.colSpan);
  const maxLayout = Math.max(0, ...(existingComponents || []).map(getComponentLayout));
  const comp = {
    id: uid('cmp'),
    type,
    colStart: grid.colStart,
    colSpan: defaults.colSpan ?? grid.colSpan,
    row: grid.row,
    rowSpan: defaults.rowSpan ?? grid.rowSpan ?? 1,
    layout: maxLayout + 1,
    props: defaultComponentProps(type),
  };
  if (Array.isArray(viewports) && viewports.length > 0 && viewports.length < VIEWPORT_IDS.length) {
    comp.viewports = viewports.filter((id) => VIEWPORT_IDS.includes(id));
  }
  return comp;
}

export function createDefaultSection(index = 0, sectionType = 'normal') {
  const type = sectionType === 'image' || sectionType === 'video' ? sectionType : 'normal';
  const labels = {
    normal: index === 0 ? 'Accueil' : `Section ${index + 1}`,
    image: index === 0 ? 'Accueil (image)' : `Section image ${index + 1}`,
    video: index === 0 ? 'Accueil (vidéo)' : `Section vidéo ${index + 1}`,
  };
  return {
    id: uid('sec'),
    sectionType: type,
    anchor: index === 0 ? 'accueil' : `section-${index + 1}`,
    label: labels[type],
    paddingY: 64,
    backgroundColor: type === 'video' ? '#000000' : '#ffffff',
    background: createSectionBackground(type),
    gridRows: type === 'image' || type === 'video' ? 32 : undefined,
    gridRowsCustom: false,
    components: [],
  };
}

export function scrollToAnchor(anchor) {
  const el = document.getElementById(anchor);
  if (!el) return;
  const root = previewScrollRootRef?.current || editorScrollRootRef?.current;
  if (root) {
    const rootRect = root.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    root.scrollTo({
      top: root.scrollTop + elRect.top - rootRect.top - 12,
      behavior: 'smooth',
    });
    return;
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function scrollToPageTop() {
  const root = previewScrollRootRef?.current || editorScrollRootRef?.current;
  if (root) {
    root.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

/** Conteneur de scroll de l'éditeur (vue tablette / téléphone). */
export const editorScrollRootRef = { current: null };

/** Conteneur de scroll de la modale d'aperçu. */
export const previewScrollRootRef = { current: null };

export function setEditorScrollRoot(el) {
  editorScrollRootRef.current = el;
}

export function setPreviewScrollRoot(el) {
  previewScrollRootRef.current = el;
}

export function sortComponentsByGrid(components, viewport = 'desktop') {
  return [...(components || [])].sort((a, b) => {
    const la = getComponentLayout(a);
    const lb = getComponentLayout(b);
    if (la !== lb) return la - lb;
    const pa = getGridPlacement(a, viewport);
    const pb = getGridPlacement(b, viewport);
    if (pa.row !== pb.row) return pa.row - pb.row;
    return pa.colStart - pb.colStart;
  });
}

export function componentStackStyle(comp, { selected = false, interacting = false, editor = false } = {}) {
  let zIndex = getComponentLayout(comp);
  if (editor) {
    if (interacting) zIndex += EDITOR_Z_INDEX.componentDraggingBoost;
    else if (selected) zIndex += EDITOR_Z_INDEX.componentSelectedBoost;
  } else if (interacting) {
    zIndex += 9000;
  } else if (selected) {
    zIndex += 8000;
  }
  return { position: 'relative', zIndex };
}
